<template>
  <v-card min-height="70vh" :class="containerClass">
    <v-card-title :class="headerClass">
      <Icon
      v-if="!loading"
      iconColor="tertiary"
      margin="mx-1"
      dataTestId="contract-detail-status"
      :icon="getStatusIcon(contract).iconName"
      :small="false"
      :tooltipText="getStatusIcon(contract).msg"
      style="line-height: 0;"
      />
      <Icon
      v-if="contract.paused"
      icon="mdi-pause-circle"
      dataTestId="contract-detail-pau"
      iconColor="tertiary"
      margin="mx-1"
      :small="false"
      :tooltipText="$t('contractOnPause')"
      style="line-height: 0;"
      />
      <span class="headline">{{ $t('contractDetails') }}</span>
      <v-spacer/>
      <v-menu bottom left>
        <template #activator="{ on }">
          <v-icon v-on="on" color="white">mdi-dots-vertical</v-icon>
        </template>
        <v-list :class="contractDetailContextMenuClass">
          <v-list-item
          v-for="(ha, index) in headerActions"
          :key="`${ha.text}-${index}`"
          @click="ha.action()"
          :disabled="ha.disabled"
          :id="`contract-detail-context-menu-${kebabCase(ha.text)}`">
            <v-avatar left>
              <v-icon color="black" :disabled="ha.disabled">{{ha.icon}}</v-icon>
            </v-avatar>
            <v-list-item-title class="subtitle-1">{{ $t(ha.text) }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <BaseDialogActions
      hideRefresh
      />
    </v-card-title>
    <v-card-text class="subtitle-1 black--text px-8 pt-2 pb-8">
      <v-container fluid fill-height v-if="loading">
        <v-row justify="center" align="center">
          <v-progress-circular
          indeterminate
          color="primary"/>
        </v-row>
      </v-container>
      <v-row class="mt-2">
        <v-col cols="12" class="mb-n6">
          <ApprovalStatusIndicator
          v-if="showApprovalStatus"
          :contractId="contract.contractId"
          :approvalStatus="contract.approvalStatus"
          @review="review"
        />
        </v-col>
          <v-col cols="12">
            <ContractInformation
            v-if="!loading"
            :contract="contract"
            :allActivities="allActivities"
            :tractLandowners="tractLandowners"
            :readonly="!canEdit"
            @edit-products="editProducts"
            />
          </v-col>
          <v-col cols="12">
            <v-row dense class="mb-n8">
              <v-col cols="auto" class="d-flex align-center">
                <span>
                  {{ $t('activities') }}
                  <Icon
                  v-if="futureScheduledRateChange !== undefined"
                  icon="invoice-text-update"
                  :small="false"
                  :tooltipText="$t('scheduledRateChange')"
                  @icon-clicked="viewScheduledRateChange"
                  ref="scheduledRateChangeIcon"
                  />
                </span>
              </v-col>
              <v-col cols="auto">
              </v-col>
              <v-spacer/>
              <v-col cols="auto">
              <RateChangeDateSelect
                :scheduledRateChanges="scheduledRateChanges"
                :contract="contract"
                @change="onRateChangeDateSelected"
              >
                <template #append-outer="{ selectedDate }">
                  <Icon
                  v-if="selectedDate.effectiveDate === $t('current')"
                  icon="mdi-pencil"
                  iconColor="success"
                  elementId="edit-contract-activities"
                  :small="false"
                  :tooltipText="$t('editActivities')"
                  @icon-clicked="editActivities"
                  :disabled="!canEdit"
                  />
                </template>
              </RateChangeDateSelect>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" class="mt-n2" v-if="!loading">
            <ContractDetailActivities
            :showActions="false"
            :allActivities="activitiesToDisplay"
            :entities="activityEntities"
            :contract="contract"
            :tract="tract"
            :selectedDate="activityEffectiveDate"
          />
          </v-col>
        </v-row>
      </v-card-text>
    <Dialog :stateId="dialogId" @close="resetDialogs" maxWidth="75vw">
      <ModificationHistory
      v-if="auditChanges"
      entityType="contract"
      :entityId="contractId"
      :entityName="$t('contract')"
      :headerClass="headerClass"
      />
      <ContractQrCode
      v-if="qrCode"
      :propContract="contractId"
      :contractMode="contractMode"
      />
      <DownloadContractDialog
      v-if="downloadContractDialogOpen"
      :contract="contract"
      :counterparties="counterparties"
      @download-contract="downloadContract"
      />
      <ContractScheduleRateChangeForm
      v-if="scheduledRateChangeDialog"
      :contract="contract"
      readonly
      />
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import { ContractStatus, ContractStatusReason, ContractMode, ContractType, UserClaims, ContractApprovalStatus, RuntimeCounterparty, ScheduledRateChangeStatus, PayBy } from '@/utils/Enumerations.js'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import RouterJump from '@/model/RouterJump.js'
import { uniqueDialogId, contractModeFor, contractTypeHasGatePass } from '@/utils/componentHelpers'
import { generateContractPdf } from '@/utils/pdf-formatters/ContractDownload'
import { generateConsolidatedContractPdf } from '@/utils/pdf-formatters/ConsolidatedContractDownload'
import { kebabCase } from 'lodash'

export default {
  name: 'ContractDetail',
  components: {
    RateChangeDateSelect: () => import('@/components/activity/RateChangeDateSelect.vue'),
    ContractDetailActivities: () => import('@/components/contract/contract-detail/ContractDetailActivities.vue'),
    ModificationHistory: () => import('@/components/admin/ModificationHistory.vue'),
    ContractInformation: () => import('@/components/contract/contract-detail/ContractInformation.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    ContractQrCode: () => import('@/components/contract/ContractQrCode.vue'),
    DownloadContractDialog: () => import('@/components/contract/DownloadContractDialog.vue'),
    ApprovalStatusIndicator: () => import('@/components/contract/contract-detail/ApprovalStatusIndicator'),
    ContractScheduleRateChangeForm: () => import('@/components/contract/scheduled-rate-change/ContractScheduleRateChangeForm.vue')
  },

  props: {
    contractId: {
      type: Number,
      required: true
    },
    hasPendingTickets: {
      type: Boolean,
      default: false
    },
    jumpsToEdit: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    loading: true,
    auditChanges: false,
    qrCode: false,
    downloadContractDialogOpen: false,
    products: [],
    contract: {},
    dialogId: uniqueDialogId('contract-audit-entries'),
    UserClaims,
    tract: {},
    tractLandowners: [],
    scheduledRateChanges: [],
    activitiesToDisplay: [],
    activityEntities: [],
    activityEffectiveDate: null,
    activityPeriod: null,
    scheduledRateChangeDialog: false
  }),

  computed: {
    ...mapGetters('activity', ['allActivities', 'activitiesLoading']),
    ...mapGetters('user', ['companyInfo', 'userInfo']),

    contractMode () {
      switch (this.contract.type) {
        case ContractType.LogYardSale.value:
          return ContractMode.LogYardSale
        case ContractType.ByproductSale.value:
        case ContractType.ByproductPurchase.value:
        case ContractType.ByproductHauling.value:
          return ContractMode.Byproducts
        case ContractType.Transfer.value:
          return ContractMode.Transfers
        case ContractType.Production.value:
        case ContractType.WoodsSale.value:
        default:
          return ContractMode.Logs
      }
    },

    headerClass () {
      return `${this.contractMode.details.color} white--text`
    },

    headerActions () {
      return (this.contract && this.tractsFetched) ? [
        {
          text: 'viewModifications',
          icon: 'mdi-history',
          action: this.viewModificationHistory,
          disabled: false
        },
        {
          text: 'edit',
          icon: 'mdi-pencil',
          action: this.editContract,
          disabled: !this.canEdit
        },
        {
          text: 'showQrCode',
          icon: 'mdi-qrcode',
          action: this.showQrCode,
          disabled: this.showQrCodeDisabled
        },
        {
          text: 'downloadContract',
          icon: 'mdi-download',
          action: this.openDownloadContractDialog,
          disabled: this.downloadContractDisabled
        },
        {
          text: 'downloadConsolidatedContract',
          icon: 'mdi-file-document-multiple-outline',
          action: this.downloadConsolidatedContract,
          disabled: this.downloadConsolidatedContractDisabled
        }
      ] : []
    },

    showQrCodeDisabled () {
      return (this.contract.isExternal && !contractTypeHasGatePass(this.contract.type)) ||
        this.contract.status === ContractStatus.Expired.value ||
        this.gatePassMissingVolumeActivity
    },

    // The kiosk needs volume pricing to create tickets from a gate pass.
    gatePassMissingVolumeActivity () {
      return [ContractType.LogYardSale.value, ContractType.Transfer.value].includes(this.contract.type) &&
        !this.allActivities.some(a => a.baseCost?.payBy === PayBy.Volume.value)
    },

    downloadContractDisabled () {
      return this.counterparties.length === 0
    },

    downloadConsolidatedContractDisabled () {
      return this.contractCounterpartyRows.length === 0
    },

    contractCounterpartyRows () {
      return this.activitiesToDisplay.flatMap(activity => {
        const isRuntime = RuntimeCounterparty.forInt(activity.runtimeCounterparty) !== undefined
        return this.counterpartyRowsFor(activity).map(row => ({ ...row, activity, isRuntime }))
      })
    },

    counterparties () {
      const accountDict = {}
      this.activitiesToDisplay.forEach(activity => {
        this.counterpartyAccountsFor(activity).forEach(({ accountId, name }) => {
          const counterparty = accountDict[accountId] ??= { accountId, name, activities: [] }
          counterparty.activities.push(activity.activityId)
        })
      })
      return Object.values(accountDict)
    },

    tractsFetched () {
      return !this.contractHasTract || this.tract.type !== undefined
    },

    showApprovalStatus () {
      return !this.loading && this.companyInfo.requireApprovalForContractModifications &&
        [
          ContractApprovalStatus.PendingModification.value,
          ContractApprovalStatus.PendingReview.value,
          ContractApprovalStatus.Returned.value
        ].includes(this.contract.approvalStatus)
    },

    canEdit () {
      if (!userAssignedClaim(UserClaims.ContractManager)) return false

      if (!this.companyInfo.requireApprovalForContractModifications) return true

      if (this.approvalStateAllowsModification) return true

      return this.previousModificationByUser
    },

    approvalStateAllowsModification () {
      return [
        ContractApprovalStatus.Approved.value,
        ContractApprovalStatus.Returned.value,
        null
      ].includes(this.contract.approvalStatus)
    },

    previousModificationByUser () {
      return this.contract.approvalStatus === ContractApprovalStatus.PendingModification.value
        ? this.contract.approvalRequestedByUserId === this.userInfo.applicationUserId
        : false
    },

    contractHasTract () {
      return !(this.contract.type === ContractType.ByproductSale.value ||
              this.contract.type === ContractType.Transfer.value ||
              this.contract.type === ContractType.LogYardSale.value ||
              this.contract.type === ContractType.ByproductHauling.value ||
              (this.contract.type === ContractType.ByproductPurchase.value && !this.contract.tractId))
    },

    containerClass () {
      const contractMode = kebabCase(contractModeFor(this.contract.type)?.label)
      return contractMode ? `contract-detail ${contractMode}` : 'contract-detail'
    },

    contractDetailContextMenuClass () {
      const contractMode = kebabCase(contractModeFor(this.contract.type)?.label)
      return contractMode ? `contract-detail-context-menu ${contractMode}` : 'contract-detail-context-menu'
    },

    futureScheduledRateChange () {
      if (!this.scheduledRateChanges) return undefined
      return this.scheduledRateChanges?.find(src => src.status === ScheduledRateChangeStatus.Pending.value)
    }
  },

  async created () {
    this.setOpenPanels([])
    await this.getContractById(this.contractId)
    if (this.contractHasTract) {
      this.tractLandowners = await this.fetchTractLandowners(this.contract.tractId)
      this.tractLandowners.sort((a, b) => b.ownership - a.ownership)
    }
    await this.getScheduledRateChanges()
  },

  methods: {
    ...mapMutations('global', ['setRouterJump']),
    ...mapMutations('activity', ['setLoading', 'setOpenPanels']),
    ...mapActions('activity', ['fetchActivities', 'fetchActivitiesByEntity']),
    ...mapActions('contract', ['getContract']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('tract', ['fetchTract', 'fetchTractLandowners', 'getLoggersForTract']),
    ...mapActions('account', ['fetchAccountById']),
    ...mapActions('scheduled-rate-change', ['fetchScheduledRateChanges']),
    userAssignedClaim,
    kebabCase,
    async getContractById (contractId) {
      this.loading = true
      try {
        const contract = await this.getContract(contractId)
        contract.hasPendingTickets = this.hasPendingTickets
        this.contract = contract

        if (this.contractHasTract) {
          const tract = await this.fetchTract(contract.tractId)
          this.tract = tract
        }

        await this.fetchActivitiesByEntity(contract.contractId)
      } finally {
        this.loading = false
      }
    },

    editActivities () {
      this.edit('edit-activities')
    },

    editProducts () {
      this.edit('edit-products')
    },

    editContract () {
      this.edit('edit-contract')
    },

    edit (editType) {
      const currentPath = this.$router.app._route.path
      if (!this.jumpsToEdit && (currentPath === '/contracts' || currentPath === '/settlements' || currentPath === '/tracts')) {
        this.$emit(editType, this.contract)
      } else {
        this.jumpToContract({
          editActivities: editType === 'edit-activities',
          editProducts: editType === 'edit-products',
          editContract: editType === 'edit-contract'
        })
      }
    },

    jumpToContract (routerJumpOptions) {
      localStorage.setItem(LocalStorageKeys.CONTRACT_GRID_TICKET_MODE, JSON.stringify(this.contractMode))
      const routerJump = new RouterJump('Tickets', 'Contracts', {
        contract: this.contract,
        ...routerJumpOptions
      })
      this.setRouterJump(routerJump)
      this.$router.push('contracts')
    },

    viewModificationHistory () {
      this.openOrUpdateDialog({ id: this.dialogId, width: '75vw' })
      this.auditChanges = true
    },

    resetDialogs () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.auditChanges = false
      this.qrCode = false
      this.downloadContractDialogOpen = false
      this.scheduledRateChangeDialog = false
    },

    closeDownloadContract () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.resetDialogs()
    },

    getStatusIcon (contract) {
      const status = contract.status
      let statusReason = ContractStatusReason.fromInt(contract.statusReason)
      if (statusReason !== 'None') {
        statusReason = `${statusReason}`
      } else {
        statusReason = ''
      }
      switch (status) {
        case 0:
          return {
            iconName: 'mdi-checkbox-marked-circle',
            color: 'primary',
            msg: this.$t('open')
          }
        case 1:
          return {
            iconName: 'mdi-close',
            color: 'error',
            msg: `${this.$t('closed')}
                  ${statusReason}`
          }
        case 2:
          return {
            iconName: 'mdi-minus-circle',
            color: 'warning',
            msg: `${this.$t('onHold')}
                  ${statusReason}`
          }
        case 3:
          return {
            iconName: 'mdi-close-circle',
            color: 'error',
            msg: `${this.$t('expired')}
                  ${statusReason}`
          }
        default:
          return {
            iconName: 'mdi-alert',
            color: 'error',
            msg: this.$t('exception')
          }
      }
    },

    showQrCode () {
      this.openOrUpdateDialog({ id: this.dialogId, width: '75vw' })
      this.qrCode = true
    },

    review (contractId) {
      this.getContractById(contractId)
      this.$emit('review')
    },

    counterpartyAccountsFor (activity) {
      const one = (accountId, name) => accountId ? [{ accountId, name }] : []
      switch (activity.runtimeCounterparty) {
        case RuntimeCounterparty.Driver.value:
          return []
        case RuntimeCounterparty.Landowner.value:
          return this.tractLandowners.map(tlo => ({ accountId: tlo.accountId, name: tlo.accountName }))
        case RuntimeCounterparty.ContractAccount.value:
          return one(this.contract.accountId, this.contract.account)
        case RuntimeCounterparty.DestinationAccount.value:
          return one(this.contract.destinationAccountId, this.contract.destination)
        case RuntimeCounterparty.DefaultLogger.value:
          return one(this.tract.loggerAccountId, this.tract.loggerAccountName)
        case RuntimeCounterparty.Hauler.value:
          return one(this.tract.haulerAccountId, this.tract.haulerAccount)
        case RuntimeCounterparty.Supplier.value:
          return one(this.tract.supplierAccountId, this.tract.supplierAccount)
        case RuntimeCounterparty.ConsultingForester.value:
          return one(this.tract.consultingForesterAccountId, this.tract.consultingForesterAccount)
        default:
          return activity.contractor ? [activity.contractor] : []
      }
    },

    counterpartyRowsFor (activity) {
      const runtime = RuntimeCounterparty.forInt(activity.runtimeCounterparty)
      if (runtime === RuntimeCounterparty.Driver) {
        return [{ key: `runtime:${runtime.value}`, name: runtime.name }]
      }

      const accounts = this.counterpartyAccountsFor(activity)
      if (!accounts.length) return [{ key: `notSet:${runtime?.value}`, name: this.$t('notSet'), role: runtime?.name }]

      if (runtime === RuntimeCounterparty.Landowner) {
        return [{ key: `runtime:${runtime.value}`, name: runtime.name }]
      }

      return accounts.map(({ accountId, name }) => ({ key: accountId, accountId, name, role: runtime?.name }))
    },

    openDownloadContractDialog () {
      if (this.counterparties.length === 1) {
        this.downloadContract(this.counterparties)
      } else {
        this.openOrUpdateDialog({ id: this.dialogId, width: '40vw' })
        this.downloadContractDialogOpen = true
      }
    },

    async downloadContract (selectedCounterparties) {
      await generateContractPdf({
        ...await this.pdfPayload(),
        activitiesByAccounts: await this.activitiesByCounterparty(selectedCounterparties)
      })
      this.closeDownloadContract()
    },

    async downloadConsolidatedContract () {
      await generateConsolidatedContractPdf({
        ...await this.pdfPayload(),
        activityRows: this.contractCounterpartyRows
      })
    },

    activitiesByCounterparty (selectedCounterparties) {
      return Promise.all(selectedCounterparties.map(async a => {
        const counterpartyActivities = this.activitiesToDisplay.filter(act => a.activities.includes(act.activityId))
        const deductingActivities = this.activitiesToDisplay.filter(act =>
          !a.activities.includes(act.activityId) && counterpartyActivities.some(ca => ca.activityId === act.deductFromActivityId))
        return {
          account: await this.accountInfo(a),
          activities: counterpartyActivities.concat(deductingActivities)
        }
      }))
    },

    async pdfPayload () {
      return {
        contract: this.contract,
        tract: this.contractHasTract ? this.tract : undefined,
        companyInfo: this.companyInfo,
        activityPeriod: this.activityPeriod,
        landowners: this.contractHasTract ? this.tractLandowners : [],
        loggers: this.contractHasTract ? await this.getLoggersForTract(this.contract.tractId) : [],
        contractMode: this.contractMode
      }
    },

    async accountInfo (account) {
      const completeCounterparty = await this.fetchAccountById(account.accountId)
      return {
        ...completeCounterparty,
        ...account
      }
    },

    async getScheduledRateChanges () {
      this.scheduledRateChanges = await this.fetchScheduledRateChanges(this.contract.contractId) || []
    },

    onRateChangeDateSelected ({ activities, entities, effectiveDate, period }) {
      this.activitiesToDisplay = activities
      this.activityEntities = entities
      this.activityEffectiveDate = effectiveDate
      this.activityPeriod = period
    },

    viewScheduledRateChange () {
      this.scheduledRateChangeDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '75vw' })
      this.$refs.scheduledRateChangeIcon.$refs.vIcon.$el.blur()
    }
  }
}
</script>
